import type { IAuthClient } from '@micro-frontend-react/employee-experience/lib/IAuthClient';
import type { SagaIterator } from 'redux-saga';
import { all, call, getContext, put, takeEvery, takeLatest } from 'redux-saga/effects';

import type { INotification } from '../Interfaces/INotification';
import { PBTApiEndpoints, PBTMessages } from './PBTConstants';
import { PBTNotificationsActionType } from '../Redux/PBT/Notifications/action-types';
import type { IMarkReadRequestAction } from '../Redux/PBT/Notifications/action-types';
import {
  receiveNotifications,
  sendNotificationsError,
  markNotificationReadSuccess,
  markAllNotificationsReadSuccess,
} from '../Redux/PBT/Notifications/actions';

function auth(authClient: IAuthClient) {
  return Promise.resolve(authClient.acquireToken(__SCOPES__));
}

interface INotificationsApiResult {
  notifications: INotification[];
  unreadCount: number;
}

/** Fetches the current user's notifications (newest first). Uses native fetch to avoid silent timeouts. */
export async function fetchNotificationsApi(token: string, timeoutMs = 15000): Promise<INotificationsApiResult> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(PBTApiEndpoints.Notifications, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      signal: controller.signal,
    });

    if (!response.ok) throw new Error(`API returned ${response.status}`);

    const json = await response.json();
    const envelope = json?.data ?? json?.Data;
    const notifications = envelope?.notifications ?? envelope?.Notifications ?? [];
    const unreadCount = envelope?.unreadCount ?? envelope?.UnreadCount ?? 0;
    return { notifications, unreadCount };
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') {
      throw new Error(`API request timed out after ${timeoutMs}ms`);
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

/** Marks a single notification as read. */
export async function markNotificationReadApi(token: string, notificationId: number, timeoutMs = 15000): Promise<void> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(PBTApiEndpoints.MarkNotificationRead(notificationId), {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      signal: controller.signal,
    });

    if (!response.ok) throw new Error(`API returned ${response.status}`);
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') {
      throw new Error(`API request timed out after ${timeoutMs}ms`);
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

/** Marks all of the current user's notifications as read. */
export async function markAllNotificationsReadApi(token: string, timeoutMs = 15000): Promise<void> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(PBTApiEndpoints.MarkAllNotificationsRead, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      signal: controller.signal,
    });

    if (!response.ok) throw new Error(`API returned ${response.status}`);
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') {
      throw new Error(`API request timed out after ${timeoutMs}ms`);
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

export function* requestNotificationsSagas(): SagaIterator {
  yield all([
    takeLatest(PBTNotificationsActionType.REQUEST_NOTIFICATIONS, fetchNotifications),
    takeEvery(PBTNotificationsActionType.MARK_READ_REQUEST, markRead),
    takeLatest(PBTNotificationsActionType.MARK_ALL_READ_REQUEST, markAllRead),
  ]);
}

function* fetchNotifications(): SagaIterator {
  const authClient: IAuthClient = yield getContext('authClient');
  try {
    const token: string = yield call(auth, authClient);
    const result: INotificationsApiResult = yield call(fetchNotificationsApi, token);
    yield put(receiveNotifications(result.notifications, result.unreadCount));
  } catch (ex) {
    yield put(sendNotificationsError(PBTMessages.NotificationsError));
  }
}

function* markRead(action: IMarkReadRequestAction): SagaIterator {
  const authClient: IAuthClient = yield getContext('authClient');
  try {
    const token: string = yield call(auth, authClient);
    yield call(markNotificationReadApi, token, action.notificationId);
    yield put(markNotificationReadSuccess(action.notificationId));
  } catch (ex) {
    yield put(sendNotificationsError(PBTMessages.NotificationsError));
  }
}

function* markAllRead(): SagaIterator {
  const authClient: IAuthClient = yield getContext('authClient');
  try {
    const token: string = yield call(auth, authClient);
    yield call(markAllNotificationsReadApi, token);
    yield put(markAllNotificationsReadSuccess());
  } catch (ex) {
    yield put(sendNotificationsError(PBTMessages.NotificationsError));
  }
}
